import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Modal, Table } from 'react-bootstrap';
import { isChangeManager } from '../changePermissions';
import { useAuth } from '../../../context/AuthContext';
import { useModal } from '../../../context/ModalContext';
import { changesApi, type ChangeCatalogKind } from '../../../services/changesApi';
import type { ChangeCatalogItem } from '../../../types/changes';
import { getApiErrorMessage } from '../../../utils/apiError';

const MAX_NAME = 255;
const MAX_DESCRIPTION = 500;

interface Props {
  kind: ChangeCatalogKind;
  title: string;
  /** Nome no singular, usado nos textos de ação ("Nova categoria"). */
  itemLabel: string;
  description: string;
  /** Dentro do hub: sem h2 de página; o título fica nas abas. */
  embedded?: boolean;
}

interface FormState {
  name: string;
  description: string;
  is_active: boolean;
}

const emptyForm = (): FormState => ({ name: '', description: '', is_active: true });

/**
 * Tela de cadastro de um catálogo do módulo de mudanças.
 *
 * Categoria e Área Solicitante têm a mesma forma e as mesmas regras, então
 * compartilham este componente — duas cópias divergiriam na primeira correção.
 * O backend é quem barra nome repetido e exclusão de item em uso; aqui a
 * mensagem dele é exibida como está.
 */
const ChangeCatalogManager: React.FC<Props> = ({
  kind,
  title,
  itemLabel,
  description,
  embedded = false,
}) => {
  const { showAlert, showConfirm } = useModal();
  const { user } = useAuth();
  // Espelha `require_catalog_manager` do backend. Esconder o botão é conforto de
  // UI; quem controla o acesso é a rota.
  const canManage = isChangeManager(user);
  const [items, setItems] = useState<ChangeCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await changesApi.listCatalog(kind);
      setItems(data.items);
      setListError(null);
    } catch (err: unknown) {
      setListError(getApiErrorMessage(err, `Não foi possível carregar ${title.toLowerCase()}.`));
    } finally {
      setLoading(false);
    }
  }, [kind, title]);

  useEffect(() => {
    void load();
  }, [load]);

  const activeCount = useMemo(() => items.filter((item) => item.is_active).length, [items]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormError(null);
    setShowModal(true);
  };

  const openEdit = (item: ChangeCatalogItem) => {
    setEditingId(item.id);
    setForm({
      name: item.name,
      description: item.description || '',
      is_active: item.is_active,
    });
    setFormError(null);
    setShowModal(true);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);

    const name = form.name.trim();
    if (name.length < 2) {
      setFormError('Informe um nome com ao menos 2 caracteres.');
      return;
    }

    const payload = {
      name,
      description: form.description.trim() || null,
      is_active: form.is_active,
    };

    setSaving(true);
    try {
      if (editingId === null) {
        await changesApi.createCatalogItem(kind, payload);
      } else {
        await changesApi.updateCatalogItem(kind, editingId, payload);
      }
      setShowModal(false);
      await load();
    } catch (err: unknown) {
      setFormError(getApiErrorMessage(err, 'Não foi possível salvar o cadastro.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: ChangeCatalogItem) => {
    const ok = await showConfirm({
      title: `Excluir ${itemLabel.toLowerCase()}`,
      message: `Tem certeza que deseja excluir "${item.name}"?`,
      variant: 'danger',
      confirmLabel: 'Excluir',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;

    try {
      await changesApi.deleteCatalogItem(kind, item.id);
      await load();
    } catch (err: unknown) {
      // O backend recusa a exclusão quando há mudanças vinculadas e explica
      // quantas são; a mensagem dele é mais útil que um texto genérico.
      showAlert({
        title: 'Não foi possível excluir',
        message: getApiErrorMessage(err, 'Não foi possível excluir o cadastro.'),
        variant: 'danger',
      });
    }
  };

  if (loading) {
    return <div className="loading-spinner">Carregando...</div>;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        {embedded ? (
          <div className="text-muted small">{description}</div>
        ) : (
          <div>
            <h2 className="mb-1">{title}</h2>
            <div className="text-muted small">{description}</div>
          </div>
        )}
        {canManage ? (
          <Button variant="success" onClick={openCreate}>
            {`Nova ${itemLabel.toLowerCase()}`}
          </Button>
        ) : null}
      </div>

      {listError ? (
        <Alert variant="danger" dismissible onClose={() => setListError(null)}>
          {listError}
        </Alert>
      ) : null}

      <Card>
        <Card.Body>
          {items.length === 0 ? (
            <div className="empty-state">
              <p>Nenhum cadastro por enquanto.</p>
            </div>
          ) : (
            <>
              <div className="text-muted small mb-2">
                {activeCount} ativo(s) de {items.length}
              </div>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Descrição</th>
                    <th>Situação</th>
                    <th className="text-end">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.name}</td>
                      <td className="text-muted">{item.description || '—'}</td>
                      <td>
                        <Badge bg={item.is_active ? 'success' : 'secondary'}>
                          {item.is_active ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </td>
                      <td className="text-end">
                        {canManage ? (
                          <>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="me-2"
                              onClick={() => openEdit(item)}
                            >
                              Editar
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => void handleDelete(item)}
                            >
                              Excluir
                            </Button>
                          </>
                        ) : (
                          <span className="text-muted small">Somente leitura</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingId === null ? `Nova ${itemLabel.toLowerCase()}` : `Editar ${itemLabel.toLowerCase()}`}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={(event) => void handleSubmit(event)}>
          <Modal.Body>
            {formError ? (
              <Alert variant="danger" dismissible onClose={() => setFormError(null)}>
                {formError}
              </Alert>
            ) : null}

            <Form.Group className="mb-3" controlId={`${kind}-name`}>
              <Form.Label>Nome</Form.Label>
              <Form.Control
                type="text"
                value={form.name}
                maxLength={MAX_NAME}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId={`${kind}-description`}>
              <Form.Label>Descrição (opcional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.description}
                maxLength={MAX_DESCRIPTION}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </Form.Group>

            <Form.Check
              type="switch"
              id={`${kind}-is-active`}
              label="Ativo (aparece no formulário de mudança)"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            <Form.Text className="text-muted">
              Desative para tirar do formulário sem perder o histórico das mudanças que já usam.
            </Form.Text>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button variant="success" type="submit" disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default ChangeCatalogManager;
